import React, { useState } from 'react';
import { ArrowLeft, Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { personaApi, PersonaAdminPayload } from '../services/personaApi';
import PersonaFormModal, { PersonaFormData } from '../components/settings/PersonaFormModal';

// ── Domain badge ───────────────────────────────────────────────────────────────

const DomainBadge: React.FC<{ label: string }> = ({ label }) => (
  <span className="px-1.5 py-0.5 text-xs rounded bg-slate-700 text-slate-300">{label}</span>
);

// ── Persona card ───────────────────────────────────────────────────────────────

interface CardProps {
  slug: string;
  name: string;
  display_title: string;
  avatar_type: string;
  domains: string[];
  is_active: boolean;
  sort_order: number;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const PersonaCard: React.FC<CardProps> = ({
  slug,
  name,
  display_title,
  avatar_type,
  domains,
  is_active,
  isFirst,
  isLast,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => (
  <div className={`flex items-center gap-4 p-4 bg-slate-800 border rounded-xl ${
    is_active ? 'border-slate-700' : 'border-slate-700/40 opacity-60'
  }`}>
    {/* Avatar placeholder */}
    <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center text-lg font-bold text-slate-300 shrink-0">
      {name.charAt(0).toUpperCase()}
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-medium text-white">{name}</span>
        <span className="text-slate-400 text-sm">·</span>
        <span className="text-slate-400 text-sm">{display_title}</span>
        <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
          is_active
            ? 'bg-emerald-500/15 text-emerald-400'
            : 'bg-slate-600/40 text-slate-500'
        }`}>
          {is_active ? 'Active' : 'Inactive'}
        </span>
        <span className="px-1.5 py-0.5 text-xs rounded bg-slate-700/60 text-slate-400">
          {avatar_type.toUpperCase()}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        <code className="text-xs text-slate-500 mr-2">{slug}</code>
        {domains.map((d) => <DomainBadge key={d} label={d} />)}
      </div>
    </div>

    {/* Actions */}
    <div className="flex items-center gap-1 shrink-0">
      <button
        onClick={onMoveUp}
        disabled={isFirst}
        title="Move up"
        className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
      >
        <ChevronUp className="w-4 h-4" />
      </button>
      <button
        onClick={onMoveDown}
        disabled={isLast}
        title="Move down"
        className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
      >
        <ChevronDown className="w-4 h-4" />
      </button>
      <button
        onClick={onEdit}
        title="Edit"
        className="p-1.5 text-slate-400 hover:text-teal-400 transition-colors"
      >
        <Pencil className="w-4 h-4" />
      </button>
      <button
        onClick={onDelete}
        title={is_active ? 'Disable persona' : 'Already inactive'}
        disabled={!is_active}
        className="p-1.5 text-slate-400 hover:text-red-400 disabled:opacity-30 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// ── Page ───────────────────────────────────────────────────────────────────────

interface PersonaRaw {
  slug: string;
  name: string;
  display_title: string;
  avatar_type: string;
  avatar_key: string | null;
  voice_provider: string;
  domains: string[];
  is_active: boolean;
  sort_order: number;
}

const PersonaSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Admin-only page — redirect non-admins back to settings
  if (user && !user.is_admin) {
    return <Navigate to="/settings" replace />;
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [editSlug, setEditSlug] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: personas = [], isLoading } = useQuery<PersonaRaw[]>({
    queryKey: ['personas-admin'],
    queryFn: () => personaApi.listAllPersonas() as Promise<PersonaRaw[]>,
  });

  // Sort locally for display (sort_order ASC)
  const sorted = [...personas].sort((a, b) => a.sort_order - b.sort_order);

  // Create / Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: PersonaFormData) => {
      const payload: PersonaAdminPayload = {
        name: data.name,
        display_title: data.display_title,
        avatar_type: data.avatar_type,
        avatar_key: data.avatar_key || null,
        voice_provider: data.voice_provider,
        voice_id: data.voice_id || null,
        system_prompt: data.system_prompt,
        tools_allowed: data.tools_allowed
          ? data.tools_allowed.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        domains: data.domains
          ? data.domains.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        is_active: data.is_active,
        sort_order: data.sort_order,
      };
      if (editSlug) {
        return personaApi.updatePersona(editSlug, payload);
      } else {
        return personaApi.createPersona({ ...payload, slug: data.slug });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas-admin'] });
      setModalOpen(false);
      setSaveError(null);
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (slug: string) => personaApi.deletePersona(slug),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['personas-admin'] }),
    onError: (err: Error) => alert(err.message),
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (order: string[]) => personaApi.reorderPersonas(order),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['personas-admin'] }),
  });

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = sorted.map((p) => p.slug);
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    reorderMutation.mutate(newOrder);
  };

  const handleMoveDown = (index: number) => {
    if (index === sorted.length - 1) return;
    const newOrder = sorted.map((p) => p.slug);
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    reorderMutation.mutate(newOrder);
  };

  const openCreate = () => {
    setEditSlug(null);
    setSaveError(null);
    setModalOpen(true);
  };

  const openEdit = (slug: string) => {
    setEditSlug(slug);
    setSaveError(null);
    setModalOpen(true);
  };

  const getInitialData = (): Partial<PersonaFormData> | undefined => {
    if (!editSlug) return undefined;
    const p = personas.find((x) => x.slug === editSlug);
    if (!p) return undefined;
    return {
      slug: p.slug,
      name: p.name,
      display_title: p.display_title,
      avatar_type: p.avatar_type || '2d',
      avatar_key: p.avatar_key || '',
      voice_provider: p.voice_provider || 'agentvibes',
      domains: (p.domains || []).join(', '),
      is_active: p.is_active,
      sort_order: p.sort_order,
    };
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">Persona Bank</h1>
            <p className="text-sm text-slate-400">Manage Expert Chat personas — create, edit, reorder</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white text-sm rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Persona
        </button>
      </div>

      {/* Persona list */}
      {isLoading ? (
        <div className="text-slate-400 text-sm">Loading personas…</div>
      ) : sorted.length === 0 ? (
        <div className="text-slate-400 text-sm">No personas found.</div>
      ) : (
        <div className="space-y-3">
          {sorted.map((p, i) => (
            <PersonaCard
              key={p.slug}
              {...p}
              isFirst={i === 0}
              isLast={i === sorted.length - 1}
              onEdit={() => openEdit(p.slug)}
              onDelete={() => deleteMutation.mutate(p.slug)}
              onMoveUp={() => handleMoveUp(i)}
              onMoveDown={() => handleMoveDown(i)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {modalOpen && (
        <PersonaFormModal
          editSlug={editSlug}
          initialData={getInitialData()}
          onSave={(data) => saveMutation.mutate(data)}
          onClose={() => { setModalOpen(false); setSaveError(null); }}
          isSaving={saveMutation.isPending}
          saveError={saveError}
        />
      )}
    </div>
  );
};

export default PersonaSettings;
